import { normalizeHealthSamples } from '../../lib/health/normalizeHealthSamples.ts';
import type { HealthMetric, CycleRecord } from '../../types/health';
export type SyncedHealth = { metrics: HealthMetric[]; cycles: CycleRecord[]; count: number };
export class HealthReadError extends Error { readonly status: number; constructor(status: number) { super("Health unavailable"); this.status = status; } }
export const emptyHealth: SyncedHealth = { metrics: [], cycles: [], count: 0 };
// Same-origin only. Tokens/health data never enter URLs, caches or localStorage.
export async function readSyncedHealth(token: string | null, signal: AbortSignal, fetcher: typeof fetch = fetch): Promise<SyncedHealth> {
  if (token !== null && !/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('Sign in again.');
  const samples: unknown[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const response = await fetcher(`/api/health/samples?limit=200${cursor ? `&cursor=${cursor}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: token ? 'omit' : 'same-origin', cache: 'no-store', redirect: 'error', signal,
    });
    if (!response.ok) throw new HealthReadError(response.status);
    const page = await response.json();
    if (!Array.isArray(page.samples) || page.samples.length > 200 || samples.length + page.samples.length > 10000) throw new Error('Too many records.');
    samples.push(...page.samples);
    cursor = page.nextCursor;
    if (cursor !== null && (typeof cursor !== 'string' || !/^[1-9]\d{0,15}$/.test(cursor) || seen.has(cursor))) throw new Error('Invalid page.');
    if (cursor) seen.add(cursor);
  } while (cursor);
  const result = normalizeHealthSamples(samples, 'display', 'Asia/Seoul');
  const metrics = result.metrics.map(m => ({ ...m, id: m.sourceId!, createdAt: m.startDate, updatedAt: m.startDate }));
  const cycles = result.cycles.map(c => ({ ...c, id: c.sourceId!, createdAt: c.date, updatedAt: c.date }));
  return { metrics, cycles, count: result.raw.length };
}
